import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import useDatasetTableFQNs from "datasets/hooks/useDatasetTableFQNs";
import { addDatasetTables } from "datasets/pages/tables/api/modifyDatasetTables";
import TableTreeView from "datasets/pages/tables/components/TableTreeView";
import { TableAccessLevel } from "datasets/types/enums/access-level";
import { Search, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "~/components/ui/popover";
import useErrorToast from "~/contexts/error-toast";
import rumClient, { trackEvent } from "~/utils/monitoring";

interface SnowflakeFormProps {
  accessLevel?: TableAccessLevel;
}

export const SnowflakeForm = ({ accessLevel }: SnowflakeFormProps) => {
  const navigate = useNavigate();
  const { datasetId } = useParams<{ datasetId: string }>();
  const { showError } = useErrorToast();
  const { mutate: mutateDataset } = useDataset();
  const { data: datasetFqns, mutate: mutateFQNs } = useDatasetTableFQNs();
  // The tree shows these locked rather than hiding them, so it needs set membership.
  const addedFqns = useMemo(() => (datasetFqns ? new Set(datasetFqns) : undefined), [datasetFqns]);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [tableSearch, setTableSearch] = useState("");
  const [tentativeSelections, setTentativeSelections] = useState<string[]>([]);
  const [selectedFqns, setSelectedFqns] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");

  const handleCancel = () => {
    setTentativeSelections(selectedFqns);
    setTableSearch("");
    setPopoverOpen(false);
  };

  const handleSelect = () => {
    setSelectedFqns(tentativeSelections);
    setTableSearch("");
    setPopoverOpen(false);
    setValidationError("");
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleCancel();
      return;
    }
    // Opening seeds the tentative picks from what is already selected, so re-opening an
    // open picker has to be a no-op — otherwise clicking back into the field to keep
    // typing would discard everything checked so far.
    if (popoverOpen) {
      return;
    }
    // Inside the guard, so focus and click on the same interaction count once.
    trackEvent("dataset_table_picker_opened", { dataset_id: datasetId });
    setTentativeSelections(selectedFqns);
    setPopoverOpen(true);
  };

  const removeSelected = (fqn: string) => {
    setSelectedFqns((prev) => prev.filter((f) => f !== fqn));
    setTentativeSelections((prev) => prev.filter((f) => f !== fqn));
  };

  const onSubmit = async () => {
    if (!datasetId) {
      return;
    }

    if (selectedFqns.length === 0) {
      setValidationError("Select at least one table.");
      return;
    }

    setSubmitting(true);
    try {
      const { added, failed } = await addDatasetTables(datasetId, selectedFqns);

      trackEvent("dataset_tables_added", {
        dataset_id: datasetId,
        count: added.length,
        ui: "nextgen",
      });

      mutateDataset();
      mutateFQNs();

      if (failed.length) {
        showError(
          `Some tables could not be added: ${failed
            .map((table) => `${table.table_fqn} (${table.reason})`)
            .join("; ")}`
        );
      }

      navigate(`/nextgen/datasets/${datasetId}/tables`);
    } catch (e: any) {
      rumClient?.recordError(e);
      showError(
        e instanceof Error ? e.message : "There was a problem trying to add tables to dataset"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-5 max-w-lg">
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium">Tables</p>
        <Popover open={popoverOpen} onOpenChange={handleOpenChange}>
          {/* An anchor rather than a trigger: a trigger toggles on click, so focusing the
              field opened the popover and the same click immediately closed it again.
              Opening is driven explicitly here instead, matching the classic form. */}
          <PopoverAnchor asChild>
            <div className="relative cursor-text" onClick={() => handleOpenChange(true)}>
              <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Select tables and views..."
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                className="pl-8"
                onFocus={() => handleOpenChange(true)}
              />
            </div>
          </PopoverAnchor>
          <PopoverContent
            className="p-0 w-[660px]"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="h-[400px] overflow-auto p-1">
              <TableTreeView
                searchTerm={tableSearch}
                selectedItems={tentativeSelections}
                setSelectedItems={setTentativeSelections}
                addedFqns={addedFqns}
                accessLevel={accessLevel}
                datasetId={datasetId}
              />
            </div>
            <div className="flex justify-end gap-2 border-t p-2">
              <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!tentativeSelections.length}
                onClick={handleSelect}
              >
                Select
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {selectedFqns.length > 0 && (
        <div className="flex flex-col gap-2">
          {selectedFqns.map((fqn) => (
            <div
              key={fqn}
              className="flex items-center justify-between rounded-xl bg-muted px-4 py-2"
            >
              <span className="text-sm">{fqn.replace(/^snowflake\./, "")}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 text-muted-foreground hover:text-foreground"
                onClick={() => removeSelected(fqn)}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {validationError && <p className="text-sm text-destructive">{validationError}</p>}

      <Button
        type="button"
        onClick={onSubmit}
        disabled={submitting || !selectedFqns.length}
        className="w-fit"
      >
        {submitting ? "Adding…" : selectedFqns.length > 1 ? "Add Tables" : "Add Table"}
      </Button>
    </div>
  );
};
